<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" scrollable max-width="640">
    <template v-if="!hideButton" v-slot:activator="{ props }">
      <v-btn
        color="primary"
        :class="{ 'float-right': floatRight, 'mx-2': floatRight }"
        v-bind="props"
      >
        <v-icon class="mr-2">mdi-package-down</v-icon>
        Entrada (compra)
      </v-btn>
    </template>
    <v-card>
      <v-toolbar color="primary" title="Entrada no estoque do grupo">
        <v-btn color="black" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text>
        <v-alert type="info" variant="tonal" density="compact" class="mb-3">
          Compra no grupo (conta a pagar + caixa). Não aloca na loja — use
          <strong>Alocar</strong> depois.
        </v-alert>
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="form.product_id"
              :items="productItems"
              item-title="title"
              item-value="value"
              label="Produto"
              :rules="[(v) => !!v || 'Campo obrigatório']"
              :loading="loadingProducts"
              :disabled="requestingApi || lockProduct"
              density="compact"
              clearable
              hide-details="auto"
              @update:model-value="onProductPicked"
            />
          </v-col>
          <v-col v-if="needsVariant" cols="12">
            <v-select
              v-model="form.product_variant_id"
              :items="variantItems"
              item-title="title"
              item-value="value"
              label="Variação"
              :rules="[(v) => !!v || 'Selecione a variação']"
              :loading="loadingVariants"
              :disabled="requestingApi || lockVariant"
              density="compact"
              clearable
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.quantity"
              label="Quantidade"
              type="number"
              min="0.001"
              step="1"
              :rules="[
                (v) =>
                  (v !== null && v !== undefined && v !== '' && Number(v) > 0) ||
                  'Informe quantidade > 0',
              ]"
              :disabled="requestingApi"
              density="compact"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.amount"
              label="Valor total R$"
              type="number"
              min="0.01"
              step="0.01"
              :rules="[
                (v) =>
                  (v !== null && v !== undefined && v !== '' && Number(v) >= 0.01) ||
                  'Informe o valor da compra',
              ]"
              :disabled="requestingApi"
              density="compact"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="6">
            <AutocompleteSelectCreateLegalPerson
              :user="user"
              label="Fornecedor"
              v-model="form.legal_person_from"
              :multiple="false"
              :rules="[(v) => !!v?.id || 'Selecione o fornecedor']"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="6">
            <AutocompleteSelectCashflowCategory
              :user="user"
              label="Rubrica"
              v-model="form.cashflow_category_id"
              :returnObject="false"
              :multiple="false"
              :clearable="false"
              :groupId="selectedGroupId"
              :rules="[(v) => !!v || 'Selecione a rubrica']"
              :types="outCategoryTypes"
            />
          </v-col>
          <v-col cols="12" md="6">
            <AutocompleteSelectCashier
              :user="user"
              label="Caixa"
              v-model="form.cashier"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="6">
            <AutocompleteSelectPaymentMethod
              :user="user"
              label="Forma de pagamento"
              v-model="form.payment_method_id"
              :returnObject="false"
              :allowOnline="false"
              density="compact"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="form.note"
              label="Observação"
              density="compact"
              :disabled="requestingApi"
              clearable
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" color="error" @click="dialog = false">Cancelar</v-btn>
        <v-btn
          color="success"
          :loading="requestingApi"
          :disabled="requestingApi"
          @click="submit"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>
          Confirmar compra
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import AutocompleteSelectCashflowCategory from './AutocompleteSelectCashflowCategory.vue'
import AutocompleteSelectCashier from './AutocompleteSelectCashier.vue'
import AutocompleteSelectCreateLegalPerson from './AutocompleteSelectCreateLegalPerson.vue'
import AutocompleteSelectPaymentMethod from './AutocompleteSelectPaymentMethod.vue'
import api from '@/services'
import { normalizeListPayload } from '@/utils/listPayload'

function emptyForm() {
  return {
    product_id: null,
    product_variant_id: null,
    quantity: null,
    amount: null,
    legal_person_from: null,
    cashflow_category_id: null,
    cashier: null,
    payment_method_id: null,
    note: null,
    product: null,
  }
}

export default {
  components: {
    AutocompleteSelectCashflowCategory,
    AutocompleteSelectCashier,
    AutocompleteSelectCreateLegalPerson,
    AutocompleteSelectPaymentMethod,
  },
  props: {
    user: Object,
    modelValue: Object,
    floatRight: Boolean,
    hideButton: Boolean,
  },
  emits: ['update:modelValue', 'created'],
  data() {
    return {
      dialog: false,
      requestingApi: false,
      loadingProducts: false,
      loadingVariants: false,
      products: [],
      variants: [],
      form: emptyForm(),
    }
  },
  computed: {
    selectedGroupId() {
      return api.storage.selectedGroup()?.id || null
    },
    outCategoryTypes() {
      // OUT + BOTH when globals available
      const dic = this.$CASH_FLOW_CATEGORY_TYPES_DIC
      if (dic?.OUT?.value != null) {
        return [dic.OUT.value, dic.BOTH?.value].filter((v) => v != null)
      }
      return undefined
    },
    lockProduct() {
      return !!(this.modelValue?.product_id || this.modelValue?.product?.id)
    },
    lockVariant() {
      return !!(this.modelValue?.product_variant_id || this.modelValue?.variant?.id)
    },
    needsVariant() {
      if (this.variants.length > 1) return true
      if (this.variants.length === 1 && !this.variants[0].is_default) return true
      return !!(this.form?.product_variant_id && this.variants.length)
    },
    variantItems() {
      return (this.variants || [])
        .filter((v) => v.active !== false)
        .map((v) => ({
          title: this.variantTitle(v),
          value: v.id,
        }))
    },
    productItems() {
      const items = (this.products || []).map((p) => ({
        title: p.name,
        value: p.id,
        product: p,
      }))
      const current = this.form?.product || this.modelValue?.product
      if (current?.id && !items.some((i) => i.value === current.id)) {
        items.unshift({ title: current.name, value: current.id, product: current })
      }
      return items
    },
  },
  methods: {
    variantTitle(v) {
      if (!v) return '—'
      if (v.name_suffix) return v.name_suffix
      if (v.is_default) return 'Padrão'
      if (Array.isArray(v.options) && v.options.length) {
        return v.options.map((o) => o.value).join(' / ')
      }
      return `Variação #${v.id}`
    },
    resetForm() {
      this.form = emptyForm()
      this.variants = []
    },
    hydrateFromModel(model) {
      if (!model || (!model.product_id && !model.product?.id && !model.product_variant_id)) {
        this.resetForm()
        return
      }
      this.form = {
        ...emptyForm(),
        product_id: model.product_id || model.product?.id || null,
        product_variant_id: model.product_variant_id || model.variant?.id || null,
        product: model.product || null,
        quantity: model.quantity != null ? model.quantity : null,
      }
      this.loadVariantsForProduct(this.form.product_id)
    },
    onProductPicked() {
      this.form.product_variant_id = null
      this.loadVariantsForProduct(this.form.product_id)
    },
    loadVariantsForProduct(productId) {
      const vm = this
      vm.variants = []
      if (!productId) return Promise.resolve()
      vm.loadingVariants = true
      return api.productVariant
        .list({ product_id: Number(productId), active: true })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.variants = Array.isArray(result)
            ? result
            : Array.isArray(items)
              ? items
              : []
          if (
            !vm.form.product_variant_id &&
            vm.variants.length === 1 &&
            vm.variants[0].is_default
          ) {
            vm.form.product_variant_id = vm.variants[0].id
          }
        })
        .catch(() => {
          vm.variants = []
        })
        .finally(() => {
          vm.loadingVariants = false
        })
    },
    loadProducts() {
      const vm = this
      if (vm.loadingProducts) return Promise.resolve()
      vm.loadingProducts = true
      return api.product
        .list({ active: true, tracks_stock: true, itemsPerPage: 200 })
        .then((result) => {
          const { items } = normalizeListPayload(result)
          vm.products = items || []
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.loadingProducts = false
        })
    },
    buildPayload() {
      const f = this.form || {}
      const payload = {
        quantity: Number(f.quantity),
        amount: Number(f.amount),
        legal_person_from: f.legal_person_from?.id
          ? { id: Number(f.legal_person_from.id) }
          : null,
        cashflow_category_id: Number(f.cashflow_category_id),
        cashier_id: f.cashier?.id != null ? Number(f.cashier.id) : null,
        payment_method_id: Number(f.payment_method_id),
      }
      if (f.product_variant_id != null && f.product_variant_id !== '') {
        payload.product_variant_id = Number(f.product_variant_id)
      }
      if (f.product_id != null && f.product_id !== '') {
        payload.product_id = Number(f.product_id)
      }
      if (f.note) payload.note = String(f.note)
      return payload
    },
    submit() {
      const vm = this
      if (vm.requestingApi) return
      if (!vm.form?.product_id && !vm.form?.product_variant_id) {
        vm.$notify({ type: 'warn', text: 'Selecione um produto' })
        return
      }
      if (vm.needsVariant && !vm.form.product_variant_id) {
        vm.$notify({ type: 'warn', text: 'Selecione a variação' })
        return
      }
      if (!vm.form.legal_person_from?.id) {
        vm.$notify({ type: 'warn', text: 'Selecione o fornecedor' })
        return
      }
      if (!vm.form.cashflow_category_id) {
        vm.$notify({ type: 'warn', text: 'Selecione a rubrica' })
        return
      }
      if (!vm.form.cashier?.id) {
        vm.$notify({ type: 'warn', text: 'Selecione o caixa' })
        return
      }
      if (!vm.form.payment_method_id) {
        vm.$notify({ type: 'warn', text: 'Selecione a forma de pagamento' })
        return
      }
      if (!(Number(vm.form.quantity) > 0) || !(Number(vm.form.amount) >= 0.01)) {
        vm.$notify({ type: 'warn', text: 'Quantidade e valor são obrigatórios' })
        return
      }
      vm.requestingApi = true
      return api.stock
        .groupIn(vm.buildPayload())
        .then((result) => {
          vm.$emit('created', result)
          vm.$notify({ type: 'success', text: 'Entrada de estoque do grupo registrada' })
          vm.dialog = false
        })
        .catch((err) => {
          if (err?.message) vm.$notify({ type: 'warn', text: err.message })
        })
        .finally(() => {
          vm.requestingApi = false
        })
    },
  },
  watch: {
    modelValue(newVal) {
      if (
        newVal &&
        (newVal.product_id || newVal.product?.id || newVal.product_variant_id) &&
        !this.dialog
      ) {
        this.hydrateFromModel(newVal)
        this.dialog = true
      }
    },
    dialog(newVal) {
      if (newVal) {
        if (
          this.modelValue &&
          (this.modelValue.product_id ||
            this.modelValue.product?.id ||
            this.modelValue.product_variant_id)
        ) {
          this.hydrateFromModel(this.modelValue)
        } else if (!this.form?.product_id) {
          this.resetForm()
        }
        this.loadProducts()
      } else {
        this.resetForm()
        this.$emit('update:modelValue', {})
      }
    },
  },
}
</script>
